import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardCopy, Loader2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { getOrderLineItemsForCopy, searchOrders } from '@/lib/api';
import { getCopiedLineItemFabricHint, getLineItemCopyKey } from '@/lib/copyLineItems';

const formatCurrency = (amount) => (
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0)
);

const CopyFromOrderDialog = ({
  open,
  onOpenChange,
  excludeOrderId,
  onConfirm,
  confirmLabel = 'Copy selected items',
}) => {
  const [step, setStep] = useState('orders');
  const [copyOrderSearch, setCopyOrderSearch] = useState('');
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [copying, setCopying] = useState(false);
  const [sourceOrder, setSourceOrder] = useState(null);
  const [sourceLineItems, setSourceLineItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const resetState = () => {
    setStep('orders');
    setCopyOrderSearch('');
    setAvailableOrders([]);
    setLoadingOrders(true);
    setLoadingItems(false);
    setCopying(false);
    setSourceOrder(null);
    setSourceLineItems([]);
    setSelectedKeys([]);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  useEffect(() => {
    if (!open || step !== 'orders') return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingOrders(true);
      try {
        const response = await searchOrders(copyOrderSearch, 50);
        if (cancelled) return;
        const orders = (response.data.data || []).filter((order) => order.order_id !== excludeOrderId);
        setAvailableOrders(orders);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch orders for copy:', error);
          setAvailableOrders([]);
        }
      } finally {
        if (!cancelled) setLoadingOrders(false);
      }
    }, copyOrderSearch ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [copyOrderSearch, open, step, excludeOrderId]);

  const handleSelectOrder = async (order) => {
    setLoadingItems(true);
    try {
      const response = await getOrderLineItemsForCopy(order.order_id);
      const items = response.data.line_items || [];
      if (items.length === 0) {
        toast.error('No line items found in the selected order');
        return;
      }
      setSourceOrder({
        order_id: response.data.order_id,
        order_number: response.data.order_number,
        customer_name: response.data.customer_name || order.customer_name,
      });
      setSourceLineItems(items);
      setSelectedKeys(items.map((item, index) => getLineItemCopyKey(item, index)));
      setStep('items');
    } catch (error) {
      toast.error('Failed to load items from that order');
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) => (
      prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key]
    ));
  };

  const allKeys = sourceLineItems.map((item, index) => getLineItemCopyKey(item, index));
  const allSelected = allKeys.length > 0 && allKeys.every((key) => selectedKeys.includes(key));

  const handleToggleAll = (checked) => {
    setSelectedKeys(checked ? allKeys : []);
  };

  const handleConfirm = async () => {
    const selectedItems = sourceLineItems.filter((item, index) => (
      selectedKeys.includes(getLineItemCopyKey(item, index))
    ));
    if (selectedItems.length === 0) {
      toast.error('Select at least one line item to copy');
      return;
    }

    setCopying(true);
    try {
      await onConfirm(selectedItems, sourceOrder);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to copy from order');
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="copy-from-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCopy className="w-5 h-5" />
            Copy Items from Existing Order
          </DialogTitle>
          <DialogDescription>
            {step === 'orders'
              ? 'Search for an order, then choose which line items to copy.'
              : `Select line items from ${sourceOrder?.order_number || 'this order'}.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'orders' ? (
          <div className="space-y-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={copyOrderSearch}
                onChange={(event) => setCopyOrderSearch(event.target.value)}
                placeholder="Search by order number or customer name..."
                className="pl-9"
                data-testid="copy-from-order-search"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {loadingOrders ? (
                <p className="text-center text-muted-foreground py-8">Loading orders...</p>
              ) : availableOrders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {copyOrderSearch ? 'No orders found' : 'No orders available'}
                </p>
              ) : (
                availableOrders.map((order) => (
                  <button
                    key={order.order_id}
                    type="button"
                    onClick={() => handleSelectOrder(order)}
                    disabled={loadingItems}
                    className="w-full p-3 rounded-md bg-secondary/30 hover:bg-secondary/50 border border-white/5 hover:border-primary/30 transition-all text-left"
                    data-testid={`copy-from-order-row-${order.order_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-mono text-sm font-medium">{order.order_number}</p>
                        <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {order.status}
                      </Badge>
                    </div>
                  </button>
                ))
              )}
            </div>
            {loadingItems && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading line items...
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleToggleAll}
                  data-testid="copy-from-order-select-all"
                />
                Select all ({sourceLineItems.length})
              </label>
              <p className="text-sm text-muted-foreground">
                {selectedKeys.length} selected
              </p>
            </div>
            <div className="max-h-[320px] overflow-y-auto space-y-2">
              {sourceLineItems.map((item, index) => {
                const key = getLineItemCopyKey(item, index);
                const fabricHint = getCopiedLineItemFabricHint(item);
                const checked = selectedKeys.includes(key);
                return (
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleKey(key)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleKey(key);
                      }
                    }}
                    className={`w-full p-3 rounded-md border text-left transition-all ${
                      checked
                        ? 'bg-primary/10 border-primary/40'
                        : 'bg-secondary/30 border-white/5 hover:border-primary/30'
                    }`}
                    data-testid={`copy-from-order-item-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={checked}
                        className="pointer-events-none mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium">
                            {item.line_item_ref ? `${item.line_item_ref} · ` : ''}
                            {item.product_name || 'Item'}
                          </p>
                          <p className="text-sm text-primary shrink-0">
                            {formatCurrency(item.line_item_price || item.base_price || 0)}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Qty {item.quantity || 1}
                          {fabricHint ? ` · ${fabricHint}` : ''}
                          {item.is_test_garment || item._is_test ? ' · Test garment' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 'items' && (
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep('orders')}
              disabled={copying}
              data-testid="copy-from-order-back"
            >
              Back
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={copying || selectedKeys.length === 0}
              data-testid="copy-from-order-confirm"
            >
              {copying ? 'Copying...' : `${confirmLabel} (${selectedKeys.length})`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CopyFromOrderDialog;
